@if (comparison(); as c) {
  <div class="page-wrapper">
    <!-- Animated Background -->
    <div class="animated-background">
      <div class="gradient-orb orb-1"></div>
      <div class="gradient-orb orb-2"></div>
      <div class="gradient-orb orb-3"></div>
      <div class="gradient-orb orb-4"></div>
    </div>

    <!-- Navigation -->
    <nav class="page-nav">
      <div class="nav-container">
        <a routerLink="/" class="nav-brand">
          <app-logo></app-logo>
        </a>
        <div class="nav-links">
          <a routerLink="/how-it-works" class="nav-link">How It Works</a>
          <a routerLink="/faq" class="nav-link">FAQ</a>
          <a routerLink="/login" class="btn-nav-primary">Get Started</a>
        </div>
      </div>
    </nav>

    <div class="page-container" #scroller>
      <main class="page-content">

        <!-- Hero -->
        <section class="hero-section">
          <div class="hero-content">
            <div class="section-badge">
              <i class="bi bi-bar-chart-steps"></i>
              Comparison
            </div>
            <h1 class="hero-title">
              Wisdone vs<br>
              <span class="gradient-text">{{ c.competitor }}</span>
            </h1>
            <p class="hero-subtitle">{{ c.tagline }}</p>
          </div>
        </section>

        <!-- Intro -->
        <section class="prose-section">
          @for (para of c.intro; track para) {
            <p class="prose-para">{{ para }}</p>
          }
        </section>

        <!-- The headline contrast -->
        <section class="headline-section">
          <div class="headline-card">
            <div class="headline-label">The difference that matters</div>
            <h2 class="headline-claim">{{ c.headline.claim }}</h2>
            <p class="headline-detail">{{ c.headline.detail }}</p>
          </div>
        </section>

        <!-- Priced comparison -->
        <section class="table-section">
          <h2 class="section-heading">What it costs</h2>
          <p class="section-sub">{{ c.pricing.scenario }}</p>

          @if (c.pricing.basis) {
            <p class="basis-note">
              <i class="bi bi-info-circle"></i>
              <span>{{ c.pricing.basis }}</span>
            </p>
          }

          <div class="table-scroll">
            <table class="compare-table">
              <thead>
                <tr>
                  <th scope="col"></th>
                  <th scope="col" class="col-us">Wisdone</th>
                  <th scope="col">{{ c.competitor }}</th>
                </tr>
              </thead>
              <tbody>
                @for (row of c.pricing.rows; track row.label) {
                  <tr>
                    <th scope="row">
                      {{ row.label }}
                      @if (row.note) {
                        <span class="row-note">{{ row.note }}</span>
                      }
                    </th>
                    <td class="col-us">{{ row.wisdone }}</td>
                    <td>{{ row.competitor }}</td>
                  </tr>
                }
              </tbody>
            </table>
          </div>

          <p class="verified">
            {{ c.competitor }} pricing verified {{ c.pricing.verifiedOn }} from the
            <a [href]="c.pricing.sourceUrl" target="_blank" rel="noopener nofollow">{{ c.pricing.sourceLabel }}</a>.
            Prices change — check before you buy, and tell us if this page is out of date.
          </p>
        </section>

        <!-- Feature differences -->
        <section class="table-section">
          <h2 class="section-heading">How they differ</h2>
          <div class="table-scroll">
            <table class="compare-table">
              <thead>
                <tr>
                  <th scope="col"></th>
                  <th scope="col" class="col-us">Wisdone</th>
                  <th scope="col">{{ c.competitor }}</th>
                </tr>
              </thead>
              <tbody>
                @for (row of c.differences; track row.dimension) {
                  <tr>
                    <th scope="row">{{ row.dimension }}</th>
                    <td class="col-us">{{ row.wisdone }}</td>
                    <td>{{ row.competitor }}</td>
                  </tr>
                }
              </tbody>
            </table>
          </div>
        </section>

        <!-- The honest section -->
        <section class="fair-section">
          <div class="fair-card">
            <h2 class="fair-title">
              <i class="bi bi-hand-thumbs-up"></i>
              {{ c.chooseThem.title }}
            </h2>
            <ul class="fair-list">
              @for (reason of c.chooseThem.reasons; track reason) {
                <li>{{ reason }}</li>
              }
            </ul>
          </div>
        </section>

        <!-- Verdict -->
        <section class="prose-section">
          <h2 class="section-heading">The short version</h2>
          <p class="prose-para verdict">{{ c.verdict }}</p>
        </section>

        <!-- Cross-links -->
        @if (others().length) {
          <section class="others-section">
            <h2 class="section-heading">Other comparisons</h2>
            <div class="others-grid">
              @for (other of others(); track other.slug) {
                <a class="other-card" [routerLink]="['/vs', other.slug]">
                  <span class="other-name">Wisdone vs {{ other.competitor }}</span>
                  <span class="other-tagline">{{ other.tagline }}</span>
                </a>
              }
            </div>
          </section>
        }

        <!-- CTA -->
        <section class="cta-section">
          <div class="cta-content">
            <h2>See it on your own website</h2>
            <p>Paste your URL and the assistant reads your site, then answers questions about it. Free, no credit card.</p>
            <div class="cta-buttons">
              <a routerLink="/" class="landing-btn-primary-gradient">
                <i class="bi bi-chat-dots"></i>
                Try it on your site
              </a>
              <a routerLink="/how-it-works" class="landing-btn-outline">
                <i class="bi bi-lightbulb"></i>
                How It Works
              </a>
            </div>
          </div>
        </section>

        <!-- Footer -->
        <footer class="page-footer">
          <div class="footer-content">
            <div class="footer-brand">
              <app-logo [showText]="false"></app-logo>
            </div>
            <div class="footer-links">
              <a routerLink="/">Home</a>
              <a routerLink="/how-it-works">How It Works</a>
              <a routerLink="/faq">FAQ</a>
              <a routerLink="/terms">Terms</a>
              <a routerLink="/privacy">Privacy</a>
            </div>
            <div class="footer-contact">
              <a href="mailto:hi@wisdone.ai">hi&#64;wisdone.ai</a>
              <span class="separator">&middot;</span>
              <a href="mailto:support@wisdone.ai">support&#64;wisdone.ai</a>
            </div>
            <div class="footer-copy">
              &copy; {{ year }} Wisdone AI. All rights reserved.
            </div>
          </div>
        </footer>
      </main>
    </div>
  </div>
}
